import React, { useState, useEffect, useCallback } from 'react';
import autoTraderService from '../services/autoTraderService';

const SETTINGS_HELP = [
  {
    key: 'dry_run',
    type: 'true / false',
    default: 'true',
    description:
      'When true the tool simulates each trade by clicking "No" on the Schwab confirmation ' +
      'dialog — no real orders are placed. Set to false only when you are ready to submit ' +
      'live trades.',
  },
  {
    key: 'ignore_large_trades',
    type: 'true / false',
    default: 'true',
    description:
      'When true any trade row whose share quantity exceeds large_trade_size is skipped ' +
      'entirely. Useful as a safeguard against runaway orders.',
  },
  {
    key: 'large_trade_size',
    type: 'integer',
    default: '1000',
    description:
      'The share-quantity threshold used by ignore_large_trades. Any trade with a quantity ' +
      'strictly greater than this number will be skipped when ignore_large_trades = true.',
  },
  {
    key: 'trade_delay_min',
    type: 'decimal (seconds)',
    default: '30.0',
    description:
      'Minimum number of seconds the tool will pause between submitting individual trades. ' +
      'A random delay between trade_delay_min and trade_delay_max is chosen for each trade ' +
      'to mimic human-like pacing.',
  },
  {
    key: 'trade_delay_max',
    type: 'decimal (seconds)',
    default: '60.0',
    description:
      'Maximum number of seconds the tool will pause between submitting individual trades. ' +
      'Must be greater than or equal to trade_delay_min.',
  },
  {
    key: 'poll_interval_seconds',
    type: 'decimal (seconds)',
    default: '20.0',
    description:
      'After all trades have been submitted the tool continues to poll the blotter for ' +
      'new order rows. This value controls how many seconds to wait between each check ' +
      'before declaring the session complete.',
  },
];

/**
 * AutoTraderPage – Download and usage guide for the AutoTrader desktop tool.
 *
 * Displays all available release versions fetched from S3, lets the user
 * download the latest build or pick a specific version, and walks them
 * through the quick-start steps needed to run the application.
 */
function AutoTraderPage() {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(null); // filename currently being downloaded
  const [showSettingsHelp, setShowSettingsHelp] = useState(false);
  const [showTradingPriority, setShowTradingPriority] = useState(false);

  const loadVersions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await autoTraderService.listVersions();
      setVersions(data.versions || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load AutoTrader versions.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVersions();
  }, [loadVersions]);

  const handleDownload = async (filename) => {
    setDownloading(filename);
    try {
      const data = await autoTraderService.getDownloadUrl(filename);
      // Open the pre-signed URL in a new tab – the browser will start the download
      window.open(data.download_url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err.response?.data?.detail || `Failed to download ${filename}.`);
    } finally {
      setDownloading(null);
    }
  };

  // Separate the "latest" entry from versioned entries
  const latestEntry = versions.find((v) => v.filename.toLowerCase().includes('latest'));
  const versionedEntries = versions
    .filter((v) => !v.filename.toLowerCase().includes('latest'))
    .sort((a, b) => b.filename.localeCompare(a.filename)); // newest version name first

  const formatBytes = (bytes) => {
    if (!bytes) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="d-flex align-items-center mb-4">
        <i className="bi bi-robot fs-2 text-primary me-3"></i>
        <div>
          <h2 className="mb-0">AutoTrader</h2>
          <p className="text-muted mb-0">Desktop automation tool for Schwab and Fidelity trading</p>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible" role="alert">
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          {error}
          <button type="button" className="btn-close" onClick={() => setError(null)}></button>
        </div>
      )}

      <div className="row g-4">
        {/* ── Download Panel ── */}
        <div className="col-12 col-lg-5">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-primary text-white">
              <i className="bi bi-download me-2"></i>
              Download
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading…</span>
                  </div>
                  <p className="mt-2 text-muted small">Fetching releases…</p>
                </div>
              ) : (
                <>
                  {/* Latest release */}
                  {latestEntry && (
                    <div className="mb-4">
                      <h6 className="text-uppercase text-muted small fw-semibold mb-2">Latest Release</h6>
                      <div className="d-flex align-items-center justify-content-between p-3 bg-light rounded">
                        <div>
                          <div className="fw-semibold">{latestEntry.filename}</div>
                          <div className="text-muted small">{formatBytes(latestEntry.size)}</div>
                          {latestEntry.release_notes && (
                            <div className="text-muted small mt-1" style={{ maxWidth: '340px' }}>
                              <i className="bi bi-journal-text me-1"></i>{latestEntry.release_notes}
                            </div>
                          )}
                        </div>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleDownload(latestEntry.filename)}
                          disabled={downloading === latestEntry.filename}
                        >
                          {downloading === latestEntry.filename ? (
                            <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                          ) : (
                            <i className="bi bi-download me-1"></i>
                          )}
                          Download
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Version history */}
                  {versionedEntries.length > 0 && (
                    <div>
                      <h6 className="text-uppercase text-muted small fw-semibold mb-2">Version History</h6>
                      <div className="list-group list-group-flush">
                        {versionedEntries.map((v) => (
                          <div
                            key={v.filename}
                            className="list-group-item list-group-item-action d-flex align-items-center justify-content-between px-0"
                          >
                            <div>
                              <div className="fw-medium">{v.filename}</div>
                              <div className="text-muted small">{formatBytes(v.size)}</div>
                              {v.release_notes && (
                                <div className="text-muted small mt-1" style={{ maxWidth: '340px' }}>
                                  <i className="bi bi-journal-text me-1"></i>{v.release_notes}
                                </div>
                              )}
                            </div>
                            <button
                              className="btn btn-outline-secondary btn-sm"
                              onClick={() => handleDownload(v.filename)}
                              disabled={downloading === v.filename}
                            >
                              {downloading === v.filename ? (
                                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                              ) : (
                                <i className="bi bi-download me-1"></i>
                              )}
                              Download
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {versions.length === 0 && !error && (
                    <p className="text-muted text-center py-3">No releases available.</p>
                  )}

                  <button
                    className="btn btn-link btn-sm text-muted mt-3 p-0"
                    onClick={loadVersions}
                  >
                    <i className="bi bi-arrow-clockwise me-1"></i>Refresh
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ── Quick-Start Guide ── */}
        <div className="col-12 col-lg-7">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-dark text-white">
              <i className="bi bi-book me-2"></i>
              Quick-Start Guide
            </div>
            <div className="card-body">
              <ol className="list-group list-group-flush" style={{ listStyle: 'none', counterReset: 'none', paddingLeft: 0 }}>
                <li className="list-group-item px-0 border-0 pb-3">
                  <div className="fw-semibold"><span className="text-muted me-2">1.</span>Download &amp; unzip</div>
                  <div className="text-muted small mt-1">
                    Download the latest release using the panel on the left, then extract the
                    zip to a folder of your choice.
                  </div>
                </li>

                <li className="list-group-item px-0 border-0 pb-3">
                  <div className="d-flex align-items-center gap-2">
                    <span className="fw-semibold"><span className="text-muted me-2">2.</span>Edit <code>user_settings.ini</code></span>
                    <button
                      className="btn btn-outline-info btn-sm py-0 px-2 d-flex align-items-center gap-1"
                      style={{ fontSize: '0.75rem' }}
                      onClick={() => setShowSettingsHelp(true)}
                      title="Show setting descriptions"
                    >
                      <i className="bi bi-info-circle"></i>
                      What do these do?
                    </button>
                  </div>
                  <div className="text-muted small mt-1">
                    Open <code>user_settings.ini</code> in the extracted folder with any text
                    editor (Notepad works fine). Update the settings with your preferences,
                    then save the file.
                  </div>
                </li>

                <li className="list-group-item px-0 border-0 pb-3">
                  <div className="fw-semibold"><span className="text-muted me-2">3.</span>Run <code>AutoTrader.exe</code></div>
                  <div className="text-muted small mt-1">
                    Double-click <code>AutoTrader.exe</code>. A command-prompt window will open
                    and stay visible in the background — keep it open throughout the session.
                  </div>
                </li>

                <li className="list-group-item px-0 border-0 pb-3">
                  <div className="fw-semibold"><span className="text-muted me-2">4.</span>Select your broker</div>
                  <div className="text-muted small mt-1">
                    The command-prompt will ask which broker to use. Press <kbd>1</kbd> for{' '}
                    <strong>Schwab</strong> or <kbd>2</kbd> for <strong>Fidelity</strong>, then
                    press <kbd>Enter</kbd>. A Chrome window will open and navigate to the chosen
                    broker's login page — log in when prompted.
                  </div>
                  <div className="text-muted small mt-2">
                    <i className="bi bi-info-circle me-1"></i>
                    You can run <code>AutoTrader.exe</code> a second time to trade on both
                    brokers simultaneously — each instance runs independently in its own window.
                  </div>
                </li>

                <li className="list-group-item px-0 border-0 pb-3">
                  <div className="fw-semibold"><span className="text-muted me-2">5.</span>Navigate to the Trade Table</div>
                  <div className="text-muted small mt-1">
                    Once logged in, navigate to the trade table so the tool can locate the
                    correct page elements.
                  </div>
                  <div className="mt-2 d-flex gap-3 flex-wrap">
                    <div>
                      <div className="text-muted small mb-1">Schwab</div>
                      <a
                        href="/images/schwab-trade-blotter.png"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="d-inline-block"
                        title="Click to view full size"
                      >
                        <img
                          src="/images/schwab-trade-blotter.png"
                          alt="Schwab trade table"
                          className="img-fluid rounded border"
                          style={{ maxHeight: '160px', cursor: 'zoom-in' }}
                        />
                      </a>
                    </div>
                    <div>
                      <div className="text-muted small mb-1">Fidelity</div>
                      <a
                        href="/images/fidelity-trade-page.png"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="d-inline-block"
                        title="Click to view full size"
                      >
                        <img
                          src="/images/fidelity-trade-page.png"
                          alt="Fidelity trade table"
                          className="img-fluid rounded border"
                          style={{ maxHeight: '160px', cursor: 'zoom-in' }}
                        />
                      </a>
                    </div>
                  </div>
                </li>

                <li className="list-group-item px-0 border-0">
                  <div className="d-flex align-items-center gap-2">
                    <span className="fw-semibold"><span className="text-muted me-2">6.</span>Press Enter to begin trading</span>
                    <button
                      className="btn btn-outline-info btn-sm py-0 px-2 d-flex align-items-center gap-1"
                      style={{ fontSize: '0.75rem' }}
                      onClick={() => setShowTradingPriority(true)}
                      title="Show trading phase breakdown"
                    >
                      <i className="bi bi-info-circle"></i>
                      How does it trade?
                    </button>
                  </div>
                  <div className="text-muted small mt-1">
                    Switch back to the AutoTrader command-prompt window and press{' '}
                    <kbd>Enter</kbd> to begin automated trade execution. The tool will take it
                    from here.
                  </div>
                </li>
              </ol>

              <div className="alert alert-warning mt-3 mb-0 small">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                <strong>Do not close the command prompt</strong> while the tool is running.
                Closing it will terminate the automation session.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Trading Priority Modal ── */}
      {showTradingPriority && (
        <>
          <div
            className="modal-backdrop fade show"
            onClick={() => setShowTradingPriority(false)}
          />
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            onClick={(e) => { if (e.target === e.currentTarget) setShowTradingPriority(false); }}
          >
            <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document">
              <div className="modal-content">
                <div className="modal-header bg-success text-white">
                  <h5 className="modal-title">
                    <i className="bi bi-list-ol me-2"></i>
                    Trading Phase Breakdown
                  </h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => setShowTradingPriority(false)}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body">
                  <p className="text-muted small mb-3">
                    AutoTrader processes trades in three phases per cycle, then repeats
                    continuously until stopped.
                  </p>
                  <div className="d-flex flex-column gap-3">
                    <div className="d-flex gap-3">
                      <div className="flex-shrink-0 text-center">
                        <span className="badge bg-warning text-dark rounded-pill px-3 py-2 fs-6">1</span>
                      </div>
                      <div>
                        <div className="fw-semibold">Sell Mutual Funds</div>
                        <div className="text-muted small mt-1">
                          All mutual fund sell orders are submitted together in a single
                          batch transaction. This happens first so proceeds are available
                          before equity trades begin. We aren't worried about price movement, so its faster to submit all MF sells at once rather than one at a time with delays in between like the equity trades (see Phase 2).
                        </div>
                      </div>
                    </div>
                    <div className="d-flex gap-3">
                      <div className="flex-shrink-0 text-center">
                        <span className="badge bg-primary rounded-pill px-3 py-2 fs-6">2</span>
                      </div>
                      <div>
                        <div className="fw-semibold">Equity Sells &amp; Buys</div>
                        <div className="text-muted small mt-1">
                          Sells are processed first, then buys — one account at a time,
                          one symbol at a time. Mutual funds and large trades (based on
                          your <code>large_trade_size</code> setting) are skipped during
                          this phase. A randomized delay between{' '}
                          <code>trade_delay_min</code> and <code>trade_delay_max</code>{' '}
                          seconds is applied between each trade.
                        </div>
                      </div>
                    </div>
                    <div className="d-flex gap-3">
                      <div className="flex-shrink-0 text-center">
                        <span className="badge bg-warning text-dark rounded-pill px-3 py-2 fs-6">3</span>
                      </div>
                      <div>
                        <div className="fw-semibold">Buy Mutual Funds</div>
                        <div className="text-muted small mt-1">
                          All mutual fund buy orders are submitted together in a single
                          batch transaction once the equity phase is complete.
                        </div>
                      </div>
                    </div>
                    <div className="alert alert-secondary mb-0 small d-flex align-items-center gap-2">
                      <i className="bi bi-arrow-repeat fs-5"></i>
                      <span>After Phase 3, the tool polls for new orders and repeats the
                      entire cycle from Phase 1 until you close the command-prompt window.</span>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowTradingPriority(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Settings Help Modal ── */}
      {showSettingsHelp && (
        <>
          {/* Backdrop */}
          <div
            className="modal-backdrop fade show"
            onClick={() => setShowSettingsHelp(false)}
          />
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            onClick={(e) => { if (e.target === e.currentTarget) setShowSettingsHelp(false); }}
          >
            <div className="modal-dialog modal-xl modal-dialog-scrollable" role="document">
              <div className="modal-content">
                <div className="modal-header bg-info text-white">
                  <h5 className="modal-title">
                    <i className="bi bi-sliders me-2"></i>
                    <code>user_settings.ini</code> — Setting Descriptions
                  </h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => setShowSettingsHelp(false)}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body p-0">
                  <table className="table table-hover mb-0 align-middle">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: '18%' }}>Setting</th>
                        <th style={{ width: '14%' }}>Type</th>
                        <th style={{ width: '10%' }}>Default</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {SETTINGS_HELP.map((s) => (
                        <tr key={s.key}>
                          <td><code>{s.key}</code></td>
                          <td className="text-muted small">{s.type}</td>
                          <td><code>{s.default}</code></td>
                          <td className="small">{s.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowSettingsHelp(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default AutoTraderPage;
